import { type MutableRefObject, useEffect, useRef } from 'react'

/** Moves the editing session into the next chain frame when typed text flows past this one. */
export function useChainOverflowHandoff({
  isEditing,
  hasNextInChain,
  onChainOverflowCaret,
  consumedLength,
  textValue,
  textareaRef,
  emitSeqRef,
}: {
  isEditing: boolean
  hasNextInChain: boolean
  onChainOverflowCaret: ((localCaretOffset: number) => boolean) | undefined
  consumedLength: number
  textValue: string
  textareaRef: MutableRefObject<HTMLTextAreaElement | null>
  emitSeqRef: MutableRefObject<number>
}) {
  /**
   * Caret follows the text across a link boundary while typing.
   *
   * Shape text overflow already *moves*: the debounced commit runs `reflowAndPersistChain`, which
   * splits at the shape's real capacity and prepends the tail to the next frame. Nothing moved the
   * caret with it, so typing past a full shape kept appending into a frame that could no longer
   * show any of it — the user's words vanished as they typed (WMPCM-9070).
   *
   * Fires only when the caret is genuinely inside the tail that flowed onward, matching the rule the
   * deleted `text-renderer.tsx` used for `overflowLocalCaretOffset`: editing mid-frame pushes
   * characters over the boundary too, but the caret stays put and must not jump.
   *
   * **De-duplicated per attempted state, NOT latched once per edit session (WMPCM-9212).** This used
   * to set a plain boolean before calling the owner and clear it only when `isEditing` went false.
   * But the owner has several states it cannot resolve and refuses the hand-off in
   * (`handleShapeChainOverflowCaret` — an unlinked frame, a chain the anchor is not in, a downstream
   * member that is not on this spread, a locked or hidden destination), and it reported none of them.
   * A single refused attempt therefore disabled the hand-off for the whole session: the caret stayed
   * in this frame, every later keystroke was inserted at the same offset and evicted again, and
   * because each eviction PREPENDS to the next frame the user watched their text arrive backwards
   * (`QAREGRESSIONTEST123` → `321TSETNOISSERGERAQ`).
   *
   * The signature is what makes retrying safe. Any real keystroke changes `selectionStart` or
   * `textValue.length`, so the next overflowing keystroke re-attempts on its own; an identical
   * re-render cannot re-fire. That holds even though a refused hand-off still flushed the reflow,
   * because reflow is idempotent by its own documented invariant (`reflowAndPersistChain` in
   * `home.tsx`) — a second pass with no user input in between changes nothing, so no new render
   * arrives to re-run this.
   *
   * `chainOverflowRelocatedRef` records the outcome for the trim-resync (`use-chain-trim-resync.ts`), which must know
   * whether a hand-off owns the caret before it decides to leave a past-the-end caret alone.
   */
  const lastOverflowAttemptRef = useRef<string | null>(null)
  const chainOverflowRelocatedRef = useRef(false)

  useEffect(() => {
    if (!isEditing) {
      lastOverflowAttemptRef.current = null
      chainOverflowRelocatedRef.current = false

      return
    }

    if (!hasNextInChain || !onChainOverflowCaret) return

    const ta = textareaRef.current

    if (!ta || ta.selectionStart !== ta.selectionEnd) return

    const consumed = consumedLength

    if (textValue.length <= consumed || ta.selectionStart <= consumed) return

    // `emitSeqRef` leads the triple, and it is what keeps this dedupe honest now that the chain
    // flows per keystroke. The comment above argues the text-derived triple is enough because "any
    // real keystroke changes `selectionStart` or `textValue.length`" — that held only while the
    // reflow was debounced. The live projection trims `textValue` back to capacity after every
    // keystroke, so a caret parked at a full frame's end produces the SAME
    // `selectionStart:consumed:length` triple on every subsequent character; the second one would be
    // declined and the WMPCM-9212 reversed-text failure would be back, this time with no locked or
    // off-spread destination needed to reach it. A write bumps the counter, an identical re-render
    // does not, which is exactly the distinction the dedupe wants.
    const signature = `${emitSeqRef.current}:${ta.selectionStart}:${consumed}:${textValue.length}`

    if (lastOverflowAttemptRef.current === signature) return

    lastOverflowAttemptRef.current = signature
    chainOverflowRelocatedRef.current = onChainOverflowCaret(ta.selectionStart - consumed) === true
  }, [
    isEditing,
    hasNextInChain,
    onChainOverflowCaret,
    consumedLength,
    textValue,
    textareaRef,
    emitSeqRef,
  ])

  return { chainOverflowRelocatedRef }
}
